import React from 'react';
import { Link } from 'react-router';
import type { LscArchiveItem } from '../../types';
import { lscPath } from '../../routing';
import ArchiveCopyLinkButton from '../ArchiveCopyLinkButton';
import ArchiveStatusBadge from '../ArchiveStatusBadge';
import AutopilotPublishedBadge, { isAutopilotPublished } from '../AutopilotPublishedBadge';
import ArchiveThumbnail from '../leagueHome/ArchiveThumbnail';
import { useLeague } from '../../hooks/useLeague';
import CfbMatchupChips from '../CfbMatchupChips';
import {
  formatArchiveCardMeta,
  formatArchiveMatchup,
  getArchiveDisplayTitle,
} from '../leagueHome/leagueHomeUtils';
import ArchiveRunTheTapeSlot from '../../../features/rtts/components/ArchiveRunTheTapeSlot';
import { isRttsEnabled } from '../../../config/featureFlags';
import { otherBroadcastLabel, otherBroadcasts } from '../leagueHome/archiveCardSources';

export type ArchiveLibraryCardProps = {
  leagueId: string;
  archive: LscArchiveItem;
  showStatus?: boolean;
};

function formatDuration(seconds?: number | null): string | null {
  if (seconds == null) return null;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatPublished(value?: string): string | null {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

const ArchiveLibraryCard: React.FC<ArchiveLibraryCardProps> = ({
  leagueId,
  archive,
  showStatus = false,
}) => {
  const { league } = useLeague();
  const title = getArchiveDisplayTitle(archive);
  const meta = formatArchiveCardMeta(archive, league?.active_edition);
  const matchup = formatArchiveMatchup(archive.match_summary);
  const awayLabel = archive.match_summary?.away_side_label;
  const homeLabel = archive.match_summary?.home_side_label;
  const duration = formatDuration(archive.duration_seconds);
  const published = formatPublished(archive.published_at);
  const detailPath = lscPath(`/leagues/${leagueId}/archive/${encodeURIComponent(archive.id)}`);
  const others = otherBroadcasts(archive);

  return (
    <article
      className="flex flex-col overflow-hidden rounded-xl border border-secondary-700 bg-secondary-800/50"
      data-testid="archive-library-card"
      data-archive-id={archive.id}
    >
      <div className="aspect-video bg-secondary-900 relative">
        <ArchiveThumbnail archive={archive} compact />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-white line-clamp-2">{title}</h3>
          {matchup !== 'League game' ? (
            league?.game_key === 'cfb' && awayLabel && homeLabel ? (
              <CfbMatchupChips awayLabel={awayLabel} homeLabel={homeLabel} separator="@" size="sm" />
            ) : (
              <p className="text-xs text-secondary-400 line-clamp-1">{matchup}</p>
            )
          ) : null}
          {meta ? <p className="text-xs text-secondary-500 line-clamp-2">{meta}</p> : null}
        </div>

        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-secondary-500">
          {duration ? <span data-testid="archive-library-duration">{duration}</span> : null}
          {published ? (
            <span data-testid="archive-library-published">Published {published}</span>
          ) : null}
        </div>

        {isAutopilotPublished(archive) ? <AutopilotPublishedBadge /> : null}

        {showStatus && archive.archive_status ? (
          <div data-testid="archive-library-status">
            <ArchiveStatusBadge status={archive.archive_status} />
          </div>
        ) : null}

        <div className="mt-auto flex flex-col gap-2 pt-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={detailPath}
              className="btn-secondary inline-flex min-h-[44px] items-center text-xs"
              data-testid="archive-library-watch-link"
            >
              Watch replay
            </Link>
            {others.map((source) => (
              <Link
                key={source.id}
                to={lscPath(`/leagues/${leagueId}/archive/${encodeURIComponent(source.id)}`)}
                className="btn-secondary inline-flex min-h-[44px] items-center text-xs"
                data-testid="archive-library-other-broadcast"
              >
                {otherBroadcastLabel(others, source)}
              </Link>
            ))}
            {archive.archive_status === 'published' ? (
              <ArchiveCopyLinkButton
                leagueId={leagueId}
                archiveId={archive.id}
                label="Copy link"
                className="min-h-[44px] text-xs"
              />
            ) : null}
          </div>
          {isRttsEnabled() ? (
            <div className="w-full max-w-full min-w-0" data-testid="archive-rtts-panel">
              <ArchiveRunTheTapeSlot
                leagueId={leagueId}
                archive={archive}
                matchupHint={matchup !== 'League game' ? matchup : undefined}
                playedAtHint={archive.published_at || archive.vod_created_at}
              />
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
};

export default ArchiveLibraryCard;
